import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import InviteCreatorDialog from "../components/InviteCreatorDialog";
import Integrations from "../components/Integrations";
import BrandPerformance from "../components/BrandPerformance";
import PayoutForm from "../components/PayoutForm";
import BrandVideos from "../components/BrandVideos";
import BrandPayoutTerms from "../components/BrandPayoutTerms";

export default function BrandDashboard() {
    const [creators, setCreators] = React.useState([]);
    const [videoCount, setVideoCount] = React.useState(0);
    const [performance, setPerformance] = React.useState({
        rows: [],
        total_spend: 0,
        total_commission: 0,
    });
    const [inviteOpen, setInviteOpen] = React.useState(false);

    const load = React.useCallback(async () => {
        const [cr, perf, brandVids] = await Promise.all([
            api.get("/brand/creators"),
            api.get("/brand/performance"),
            api.get("/brand/videos"),
        ]);
        setCreators(cr.data);
        setPerformance(perf.data);
        setVideoCount(brandVids.data.length);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    // Return-from-Stripe polling for funded payouts (kept active for ?fund_session_id=...)
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("fund_session_id");
        const cancelled = params.get("fund_cancelled");
        if (cancelled) {
            toast.info("Payment cancelled");
            sessionStorage.removeItem("castline_pending_fund");
            window.history.replaceState({}, "", "/brand");
            return;
        }
        if (!sessionId) return;
        let attempts = 0;
        const max = 6;
        const tick = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    toast.success("Payout funded · creator notified");
                    sessionStorage.removeItem("castline_pending_fund");
                    window.history.replaceState({}, "", "/brand");
                    load();
                    return;
                }
                if (data.status === "expired") {
                    toast.error("Checkout expired");
                    window.history.replaceState({}, "", "/brand");
                    return;
                }
                attempts += 1;
                if (attempts >= max) {
                    toast.info("Still processing — check again in a minute");
                    window.history.replaceState({}, "", "/brand");
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                toast.error("Could not verify payment");
            }
        };
        toast.info("Verifying payment…");
        tick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="brand-dashboard">
            <Navbar />

            <section className="border-b hairline surface-warm">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="chip chip-coral inline-flex mb-3">BRAND</div>
                        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                            Your creator library
                        </h1>
                        <p className="text-slate-500 mt-2 max-w-xl">
                            Get creator content. Use it your way. Pay on performance.
                        </p>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        <button
                            onClick={() => setInviteOpen(true)}
                            className="btn-pill-primary inline-flex items-center"
                            data-testid="invite-creator-cta"
                        >
                            <UserPlus className="w-4 h-4 mr-1.5" /> Invite creator
                        </button>
                    </div>
                </div>
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    ["Creators", creators.length, "lime"],
                    ["Videos", videoCount, "lime"],
                    ["Tracked revenue", `$${performance.total_spend?.toLocaleString?.() || 0}`, "coral"],
                    ["Paid to creators", `$${performance.total_commission?.toLocaleString?.() || 0}`, "coral"],
                ].map(([l, v, accent]) => (
                    <div key={l} className="card-ecom p-5">
                        <div className="text-xs text-slate-500">{l}</div>
                        <div
                            className={`font-display text-3xl font-semibold mt-1 ${accent === "coral" ? "text-coral" : ""}`}
                        >
                            {v}
                        </div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-6">
                <Tabs defaultValue="videos" className="w-full">
                    <TabsList
                        className="rounded-full bg-slate-100 p-1 h-auto flex flex-wrap"
                        data-testid="brand-tabs"
                    >
                        {[
                            ["videos", "Videos"],
                            ["creators", "Creators"],
                            ["payout-terms", "Payout terms"],
                            ["performance", "Performance"],
                            ["integrations", "Integrations"],
                            ["payouts", "Payment method"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-5 py-2"
                                data-testid={`tab-${v}`}
                            >
                                {l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="videos" className="mt-6">
                        <BrandVideos />
                    </TabsContent>

                    <TabsContent value="creators" className="mt-6">
                        {creators.length === 0 ? (
                            <div className="card-ecom p-12 text-center" data-testid="no-creators-tab">
                                <UserPlus className="w-8 h-8 text-coral mx-auto mb-3" />
                                <p className="text-slate-600 mb-3">
                                    Bring your existing creator partners onto Castline.
                                </p>
                                <button
                                    onClick={() => setInviteOpen(true)}
                                    className="btn-pill-primary inline-flex items-center"
                                    data-testid="empty-invite-creator"
                                >
                                    <UserPlus className="w-4 h-4 mr-1.5" /> Invite a creator
                                </button>
                            </div>
                        ) : (
                            <div
                                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                                data-testid="creators-tab-list"
                            >
                                {creators.map((c) => (
                                    <Link
                                        key={c.id}
                                        to={`/creators/${c.id}`}
                                        className="card-ecom p-5"
                                        data-testid={`brand-creator-${c.id}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold text-xl flex-shrink-0">
                                                {c.name?.[0]?.toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-display font-semibold text-slate-900 truncate">
                                                    {c.name}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate">
                                                    {c.email}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center mt-4">
                                            <span className="chip">{c.video_count} videos</span>
                                            <span className="chip chip-lime">CONNECTED</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="payout-terms" className="mt-6">
                        <BrandPayoutTerms />
                    </TabsContent>

                    <TabsContent value="performance" className="mt-6">
                        <BrandPerformance />
                    </TabsContent>

                    <TabsContent value="integrations" className="mt-6">
                        <Integrations />
                    </TabsContent>

                    <TabsContent value="payouts" className="mt-6">
                        <PayoutForm />
                    </TabsContent>
                </Tabs>
            </section>

            <InviteCreatorDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onCreated={load}
            />
        </div>
    );
}
